import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';

import ReservationService from '~/services/ReservationService';

export type RatePlanStatus = 'content-approved' | 'draft';

export interface ChainRatePlan {
  id: string;
  rate_plan_code: string | null;
  name: string;
  markup: number | null;
  rate_type: string | null;
  status: string;
  source: string;
  id_salesforce_external: string;
  properties: { id: string; name: string }[];
}

interface ChainRatePlansResponse {
  status: number;
  result: ChainRatePlan[];
  count: number;
  total?: number;
}

export interface ChainRatePlansFilters {
  status: RatePlanStatus;
  page: number;
  limit: number;
  ratePlanCode?: string;
  propertySearch?: string;
}

export const chainRatePlanQueryKeys = {
  all: ['chains', 'ratePlans'] as const,
  list: (chainId: string, filters: ChainRatePlansFilters) => [...chainRatePlanQueryKeys.all, chainId, filters] as const,
};

export function useGetChainRatePlans(chainIds: string[], filters: ChainRatePlansFilters) {
  const queryResults = useQueries({
    queries: chainIds.map((id) => ({
      queryKey: chainRatePlanQueryKeys.list(id, filters),
      queryFn: ({ signal: _signal }) =>
        ReservationService.getRatePlansByChain(
          id,
          filters.status,
          filters.page,
          filters.limit,
          filters.ratePlanCode,
          filters.propertySearch,
          // TODO: pass signal to service once it supports AbortSignal
        ) as Promise<ChainRatePlansResponse>,
      enabled: chainIds.length > 0,
      placeholderData: (prev: ChainRatePlansResponse | undefined) => prev,
    })),
  });

  const isLoading = queryResults.some((q) => q.isLoading);
  const error = queryResults.find((q) => q.error)?.error ?? null;

  // TODO: Pagination is broken for multi-ID chains (e.g. ACCOR has 4 IDs). Each query fires with the
  // same page/limit, so a single "page" can return up to 4×limit items and totalCount is the sum of
  // all 4 totals, making pageCount far larger than expected. This requires backend support for a
  // multi-chain-ID endpoint (or a single query with multiple IDs) to fix properly.
  const { ratePlans, totalCount } = useMemo(() => {
    const plans = queryResults.flatMap((q) => q.data?.result ?? []);
    const total = queryResults.reduce((sum, q) => sum + (q.data?.total ?? q.data?.count ?? 0), 0);
    return { ratePlans: plans, totalCount: total };
  }, [queryResults]);

  return { ratePlans, totalCount, isLoading, error };
}
